import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { duelActivityService } from '../../../services/socialService';
import { useDuelRealtime } from '../../../hooks/useDuelRealtime';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const DuelActivityFeed = ({ duelId, userId, proofs = [] }) => {
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (duelId) {
      loadActivity();
    }
  }, [duelId]);

  // Subscribe to real-time activity updates
  useDuelRealtime(duelId, userId, {
    onCommentAdded: async (payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        // Fetch full comment with user info
        try {
          const commentsData = await duelActivityService?.getDuelComments(duelId);
          setComments(commentsData || []);
        } catch (error) {
          console.error('Error refetching comments:', error);
        }
      }
    },
    onReactionAdded: async (payload) => {
      if (payload?.new || payload?.old) {
        // Refetch reactions to get updated counts
        try {
          const reactionsData = await duelActivityService?.getDuelReactions(duelId);
          setReactions(reactionsData || []);
        } catch (error) {
          console.error('Error refetching reactions:', error);
        }
      }
    }
  });

  const loadActivity = async () => {
    setLoading(true);
    try {
      const [commentsData, reactionsData] = await Promise.all([
        duelActivityService?.getDuelComments(duelId),
        duelActivityService?.getDuelReactions(duelId)
      ]);
      setComments(commentsData || []);
      setReactions(reactionsData || []);
    } catch (error) {
      console.error('Error loading activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment?.trim()) return;

    setSubmitting(true);
    try {
      const comment = await duelActivityService?.createComment(duelId, userId, newComment);
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddReaction = async (reactionType, proofId = null) => {
    try {
      const reaction = await duelActivityService?.addReaction(duelId, userId, reactionType, proofId);
      if (reaction) {
        setReactions(prev => [...prev, reaction]);
      } else {
        // Reaction was removed (toggle)
        setReactions(prev => prev?.filter(r => 
          !(r?.user_id === userId && r?.reaction_type === reactionType && r?.proof_id === proofId)
        ));
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const reactionIcons = {
    fire: 'Flame',
    clap: 'Hand',
    muscle: 'Dumbbell',
    trophy: 'Trophy',
    laugh: 'Smile'
  };

  const reactionEmojis = {
    fire: '🔥',
    clap: '👏',
    muscle: '💪',
    trophy: '🏆',
    laugh: '😂'
  };

  const getReactionCount = (reactionType, proofId = null) => {
    return reactions?.filter(r => r?.reaction_type === reactionType && r?.proof_id === proofId)?.length;
  };

  const hasUserReacted = (reactionType, proofId = null) => {
    return reactions?.some(r => r?.user_id === userId && r?.reaction_type === reactionType && r?.proof_id === proofId);
  };

  // Combine proofs and comments into timeline
  const timeline = [
    ...proofs?.map(p => ({ type: 'proof', data: p, timestamp: p?.created_at })),
    ...comments?.map(c => ({ type: 'comment', data: c, timestamp: c?.created_at }))
  ]?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="space-y-4">
      {/* Activity Timeline */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Activity" size={16} />
          <span>Actividad del Duelo</span>
        </h4>

        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" size={24} className="text-muted-foreground animate-spin mx-auto" />
          </div>
        ) : timeline?.length === 0 ? (
          <div className="text-center py-8 bg-muted/30 rounded-xl">
            <Icon name="MessageSquare" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No hay actividad aún</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            <AnimatePresence>
              {timeline?.map((item, index) => (
                <motion.div
                  key={`${item?.type}-${item?.data?.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-3 bg-muted/30 rounded-lg border border-border"
                >
                  {item?.type === 'proof' ? (
                    <div>
                      <div className="flex items-start space-x-3 mb-2">
                        {item?.data?.user?.avatar_url ? (
                          <img src={item?.data?.user?.avatar_url} alt={item?.data?.user?.full_name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary">
                              {item?.data?.user?.full_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="text-sm font-semibold text-foreground">{item?.data?.user?.full_name}</p>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true, locale: es })}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                            <Icon name="Camera" size={12} />
                            <span>Subió evidencia</span>
                          </div>
                          {item?.data?.photo_url && (
                            <img src={item?.data?.photo_url} alt="Proof" className="mt-2 rounded-lg w-full max-w-xs" />
                          )}
                        </div>
                      </div>
                      {/* Reactions for this proof */}
                      <div className="flex items-center space-x-2 ml-11 mt-2">
                        {Object.keys(reactionEmojis)?.map(reactionType => {
                          const count = getReactionCount(reactionType, item?.data?.id);
                          const isActive = hasUserReacted(reactionType, item?.data?.id);
                          return (
                            <button
                              key={reactionType}
                              onClick={() => handleAddReaction(reactionType, item?.data?.id)}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                                isActive 
                                  ? 'bg-primary/20 text-primary border border-primary/30' :'bg-muted hover:bg-muted/70 text-muted-foreground border border-border'
                              }`}
                            >
                              {reactionEmojis?.[reactionType]} {count > 0 && count}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start space-x-3">
                      {item?.data?.user?.avatar_url ? (
                        <img src={item?.data?.user?.avatar_url} alt={item?.data?.user?.full_name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-semibold text-primary">
                            {item?.data?.user?.full_name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="text-sm font-semibold text-foreground">{item?.data?.user?.full_name}</p>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true, locale: es })}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{item?.data?.comment_text}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
      {/* Add Comment */}
      <div className="space-y-2">
        <div className="flex space-x-2">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e?.target?.value)}
            placeholder="Escribe un comentario..."
            onKeyPress={(e) => e?.key === 'Enter' && handleAddComment()}
            disabled={submitting}
          />
          <Button
            onClick={handleAddComment}
            disabled={!newComment?.trim() || submitting}
            loading={submitting}
            size="sm"
          >
            <Icon name="Send" size={16} />
          </Button>
        </div>

        {/* Quick Reactions */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground">Reacciones rápidas:</span>
          {Object.keys(reactionEmojis)?.map(reactionType => {
            const count = getReactionCount(reactionType);
            const isActive = hasUserReacted(reactionType);
            return (
              <button
                key={reactionType}
                onClick={() => handleAddReaction(reactionType)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  isActive 
                    ? 'bg-primary/20 text-primary border border-primary/30' :'bg-muted hover:bg-muted/70 text-muted-foreground border border-border'
                }`}
              >
                {reactionEmojis?.[reactionType]} {count > 0 && count}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DuelActivityFeed;